import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { historyPath, type ClientKindConfig } from "@/features/clients/kinds";
import {
  farmingHistorySchema,
  type ClientRecord,
  type StatusChange,
} from "@/features/clients/schemas";
import { activitySchema } from "@/features/units/schemas";
import { downloadFile } from "@/shared/api/files";
import { request } from "@/shared/api/http";

export const clientKeys = {
  all: (config: ClientKindConfig) => ["clients", config.kind] as const,
  list: (config: ClientKindConfig) => ["clients", config.kind, "list"] as const,
  detail: (config: ClientKindConfig, id: number) =>
    ["clients", config.kind, "detail", String(id)] as const,
};

export function useClients(config: ClientKindConfig) {
  // Leads arrive by rotation and assignment with a short Fresh timer: keep the list current
  // even in a background tab, so the board and the new-leads notice see them within a minute.
  const live = config.kind === "lead";
  return useQuery({
    queryKey: clientKeys.list(config),
    queryFn: ({ signal }) =>
      request(config.api, { schema: z.array(config.itemSchema), signal }) as Promise<
        ClientRecord[]
      >,
    refetchInterval: live ? 60_000 : false,
    refetchIntervalInBackground: live,
  });
}

export function useClient(config: ClientKindConfig, id: number) {
  const queryClient = useQueryClient();
  return useQuery<ClientRecord>({
    queryKey: clientKeys.detail(config, id),
    queryFn: ({ signal }) =>
      request(`${config.api}/${String(id)}`, {
        schema: config.itemSchema,
        signal,
      }) as Promise<ClientRecord>,
    placeholderData: () =>
      queryClient
        .getQueryData<ClientRecord[]>(clientKeys.list(config))
        ?.find((record) => record.id === id),
  });
}

/** Status changes over time: from the history endpoint, or farming's embedded history. */
export function useStatusHistory(config: ClientKindConfig, id: number) {
  return useQuery({
    queryKey: [...clientKeys.detail(config, id), "history"],
    queryFn: async ({ signal }): Promise<StatusChange[]> => {
      const path = historyPath(config, id);
      if (path && config.historySchema) {
        return request(path, { schema: z.array(config.historySchema), signal }) as Promise<
          StatusChange[]
        >;
      }
      const farming = await request(`farming/${String(id)}`, {
        schema: z.looseObject({
          farming: z.looseObject({ status_history: z.array(farmingHistorySchema).default([]) }),
        }),
        signal,
      });
      return farming.farming.status_history;
    },
  });
}

export function useClientActivities(config: ClientKindConfig, id: number) {
  return useQuery({
    queryKey: [...clientKeys.detail(config, id), "activities"],
    queryFn: ({ signal }) =>
      request(`${config.api}/${String(id)}/activities`, {
        schema: z.array(activitySchema),
        signal,
      }),
  });
}

export function useChangeStatus(config: ClientKindConfig) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      statusId,
      details,
    }: {
      id: number;
      statusId: number;
      /** Reservation (developer_id, project_id, unit_number) or closing (closed_at) details. */
      details?: Record<string, string | number | null>;
    }) =>
      request(`${config.api}/${String(id)}/status`, {
        method: config.statusMethod,
        json: { status_id: statusId, ...details },
        schema: z.unknown(),
      }),
    onMutate: ({ id, statusId }) => moveOptimistically(queryClient, config, [id], statusId),
    onError: (_error, _vars, previous) => {
      queryClient.setQueryData(clientKeys.list(config), previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: clientKeys.all(config) });
      void queryClient.invalidateQueries({ queryKey: ["lead-pool"] });
    },
  });
}

/** The most leads one bulk status request takes (the API refuses more). */
const BULK_LIMIT = 500;

/**
 * Move several records to one status. Leads go in all-or-none requests of up to 500; cold calls
 * and resale owners have no bulk endpoint, so each moves on its own.
 */
export function useBulkChangeStatus(config: ClientKindConfig) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, statusId }: { ids: number[]; statusId: number }) => {
      if (config.kind === "lead") {
        for (let start = 0; start < ids.length; start += BULK_LIMIT) {
          await request(`${config.api}/status`, {
            method: "POST",
            json: { lead_ids: ids.slice(start, start + BULK_LIMIT), status_id: statusId },
            schema: z.unknown(),
          });
        }
        return;
      }
      await Promise.all(
        ids.map((id) =>
          request(`${config.api}/${String(id)}/status`, {
            method: config.statusMethod,
            json: { status_id: statusId },
            schema: z.unknown(),
          }),
        ),
      );
    },
    onMutate: ({ ids, statusId }) => moveOptimistically(queryClient, config, ids, statusId),
    onError: (_error, _vars, previous) => {
      queryClient.setQueryData(clientKeys.list(config), previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: clientKeys.all(config) });
      void queryClient.invalidateQueries({ queryKey: ["lead-pool"] });
    },
  });
}

/** The cards move at once; their timers show as fresh until the server sends the deadlines. */
async function moveOptimistically(
  queryClient: QueryClient,
  config: ClientKindConfig,
  ids: readonly number[],
  statusId: number,
) {
  await queryClient.cancelQueries({ queryKey: clientKeys.list(config) });
  const previous = queryClient.getQueryData<ClientRecord[]>(clientKeys.list(config));
  const moving = new Set(ids);
  const now = new Date().toISOString();
  queryClient.setQueryData<ClientRecord[]>(clientKeys.list(config), (rows) =>
    rows?.map((row) =>
      moving.has(row.id)
        ? {
            ...row,
            statusId,
            journey: row.journey
              ? { ...row.journey, deadlineAt: null, statusChangedAt: now }
              : null,
          }
        : row,
    ),
  );
  return previous;
}

export function useAssignClients(config: ClientKindConfig) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      agentIds,
      statusId = null,
    }: {
      ids: number[];
      agentIds: number[];
      /** Leads only: the stage they land on; null keeps each lead's own. */
      statusId?: number | null;
    }) =>
      request(`${config.api}/assign`, {
        method: "POST",
        json: {
          [config.assign.ids]: ids,
          [config.assign.agents]: agentIds,
          ...(statusId === null ? {} : { status_id: statusId }),
        },
        schema: z.unknown(),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: clientKeys.all(config) }),
  });
}

export function useSaveClient(config: ClientKindConfig) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: number; body: Record<string, unknown> }) =>
      id === undefined
        ? request(config.api, { method: "POST", json: body, schema: z.unknown() })
        : request(`${config.api}/${String(id)}`, {
            method: "PUT",
            json: body,
            schema: z.unknown(),
          }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: clientKeys.all(config) }),
  });
}

export function useDeleteClient(config: ClientKindConfig) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      request(`${config.api}/${String(id)}`, { method: "DELETE", schema: z.unknown() }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: clientKeys.all(config) }),
  });
}

export function useImportClients(config: ClientKindConfig) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, agentIds }: { file: File; agentIds: number[] }) =>
      request(`${config.api}/import`, {
        method: "POST",
        form: {
          file,
          ...(config.importAgents
            ? config.kind === "lead"
              ? { [config.importAgents]: agentIds[0] ?? null }
              : { [config.importAgents]: agentIds }
            : {}),
        },
        schema: z.unknown(),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: clientKeys.all(config) }),
  });
}

export function downloadClientExport(config: ClientKindConfig): Promise<string> {
  return downloadFile(`${config.api}/export`);
}

export function downloadClientTemplate(config: ClientKindConfig): Promise<string> {
  return downloadFile(`${config.api}/template/download`);
}
